import { useEffect, useState } from 'react';
import type { KnowledgeFinalizeResult, KnowledgeItem, KnowledgeItemPage, Problem } from '../api/types';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { EmptyState } from '../components/EmptyState';
import { ProblemNotice } from '../components/ProblemNotice';
import { Spinner } from '../components/Spinner';
import { Table } from '../components/Table';
import { Tabs } from '../components/Tabs';
import { Link } from '../app/Link';
import { usePlatform } from '../app/platform';
import { KNOWLEDGE_TABS, type KnowledgeRoute, type KnowledgeTab, type Route } from '../app/router';
import { knowledgeItemStatusView } from '../app/status';
import { type AsyncState, toProblem, useAsync } from '../app/useAsync';
import { useStudioEvents } from '../app/useStudioEvents';
import { formatDateTime } from '../lib/datetime';
import { saveBytes } from '../lib/download';
import { DeleteDialog } from './DeleteDialog';
import { KnowledgeSourcesPanel } from './KnowledgeSourcesPanel';
import { ConversationSummaryPanel } from './ConversationSummaryPanel';
import { MemoryPanel } from './MemoryPanel';

export interface KnowledgePageProps {
  route: KnowledgeRoute;
  navigate(route: Route, options?: { replace?: boolean }): void;
}

/** 置けるファイルの拡張子。API（studio/domain/knowledge.py の MEDIA_TYPES）と同じ集合。 */
const ACCEPT = '.pdf,.md,.txt,.html,.htm,.csv,.json,.png,.jpg,.jpeg,.gif,.webp,.docx,.xlsx,.pptx';

const TAB_LABELS: Record<KnowledgeTab, string> = { files: 'ファイル', summary: '会話まとめ', memories: '記憶' };

function tabRoute(workspaceId: string, key: string): KnowledgeRoute {
  return key === 'summary' || key === 'memories' ? { name: 'knowledge', workspaceId, tab: key } : { name: 'knowledge', workspaceId };
}

/**
 * Workspace の知識ソース（計画の調整 A11）。アップロードしたファイルは解析しないので、
 * 出すのは名前・形式・大きさ・状態だけ。中身は「原本を開く」でダウンロードする。
 */
export function KnowledgePage({ route, navigate }: KnowledgePageProps) {
  const { api } = usePlatform();
  const { workspaceId } = route;
  const sources = useAsync(() => api.listKnowledgeSources(workspaceId), [api, workspaceId]);
  const items = useAsync(() => api.listKnowledgeItems(workspaceId, { category: 'file' }), [api, workspaceId]);
  const eventCursor = items.data?.eventCursor ?? null;
  // 会話まとめ・記憶のタブは各パネルが自分で購読するので、ここはファイルのタブのときだけ張る（2 本にしない）。
  const filesTab = route.tab === undefined;
  const streamState = useStudioEvents({
    api,
    workspaceId,
    cursor: eventCursor,
    enabled: eventCursor !== null && filesTab,
    // Studio の作業中は studio.message が 1 秒ごとに流れる。ファイルの状態と参照の数が変わるイベントだけで取り直す。
    onEvent: (event) => {
      if (event.type === 'studio.knowledge' || event.type === 'studio.revision') items.reload();
      // 接続の作成・更新・削除（S1 の studio.knowledge）でソースの一覧も取り直す。
      if (event.action?.startsWith('source_')) sources.reload();
    },
  });

  useEffect(() => {
    if (streamState === 'expired') items.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamState]);

  return (
    <div className="page">
      <p>
        <Link to={{ name: 'workspace', workspaceId }} onNavigate={navigate}>
          Workspaceへ戻る
        </Link>
      </p>

      <Card title="知識ソース">
        <KnowledgeSourcesPanel workspaceId={workspaceId} state={sources} />
      </Card>

      <Tabs
        label="知識ソースの表示"
        active={route.tab ?? 'files'}
        onSelect={(key) => navigate(tabRoute(workspaceId, key), { replace: true })}
        items={KNOWLEDGE_TABS.map((key) => ({ key, label: TAB_LABELS[key] }))}
      />

      {route.tab === 'summary' ? (
        <ConversationSummaryPanel workspaceId={workspaceId} />
      ) : route.tab === 'memories' ? (
        <MemoryPanel workspaceId={workspaceId} />
      ) : (
        <>
          <AddFilesPanel workspaceId={workspaceId} onFinished={items.reload} />

          <Card title="アップロードしたファイル">
            <SearchForm
              query={route.q ?? ''}
              onSearch={(q) => navigate(q ? { ...route, q } : { name: 'knowledge', workspaceId }, { replace: true })}
            />
            {route.q ? (
              <SearchResults workspaceId={workspaceId} query={route.q} />
            ) : (
              <FileList workspaceId={workspaceId} state={items} />
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function AddFilesPanel({ workspaceId, onFinished }: { workspaceId: string; onFinished(): void }) {
  const { api } = usePlatform();
  const [files, setFiles] = useState<File[]>([]);
  // file input の選択は value で消しきれないので、key を変えて作り直す。
  const [inputKey, setInputKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [outcome, setOutcome] = useState<{ result: KnowledgeFinalizeResult; names: Map<string, string> } | null>(null);

  const submit = async () => {
    if (files.length === 0) return;
    setSubmitting(true);
    setProblem(null);
    setOutcome(null);
    try {
      const { result, uploads } = await api.uploadKnowledgeFiles(workspaceId, files);
      setOutcome({ result, names: new Map(uploads.map((upload) => [upload.fileId, upload.name])) });
      // 押し直して同じ内容の版を積まないよう、選択を空にする。
      setFiles([]);
      setInputKey((key) => key + 1);
    } catch (error) {
      setProblem(toProblem(error));
    } finally {
      setSubmitting(false);
      onFinished();
    }
  };

  return (
    <Card title="ファイルを追加">
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <label className="field">
          <span className="field-label">追加するファイル</span>
          <input
            key={inputKey}
            className="input"
            type="file"
            multiple
            accept={ACCEPT}
            onChange={(event) => setFiles(Array.from(event.target.files ?? []))}
          />
        </label>
        <ProblemNotice problem={problem} />
        <div className="row">
          <Button type="submit" variant="primary" disabled={submitting || files.length === 0}>
            {submitting ? '送信中…' : '追加する'}
          </Button>
          <span className="hint">1 回 25 件、1 件 25 MiB まで。中身は解析せず、そのまま保存します。同じ名前は置き換えます。</span>
        </div>
        {outcome && (
          <div className="stack">
            <p>{outcome.result.success} 件を追加しました</p>
            {outcome.result.invalids.length > 0 && (
              <ul>
                {outcome.result.invalids.map((invalid) => (
                  <li key={invalid.fileId}>
                    {`${outcome.names.get(invalid.fileId) ?? invalid.fileId}: ${invalid.reason}`}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </form>
    </Card>
  );
}

function SearchForm({ query, onSearch }: { query: string; onSearch(q: string): void }) {
  const [value, setValue] = useState(query);
  useEffect(() => setValue(query), [query]);
  return (
    <form
      className="row row-fill"
      onSubmit={(event) => {
        event.preventDefault();
        onSearch(value.trim());
      }}
    >
      <input
        className="input"
        type="search"
        value={value}
        aria-label="ファイル名で検索"
        placeholder="ファイル名で検索"
        onChange={(event) => setValue(event.target.value)}
      />
      <Button type="submit">検索</Button>
    </form>
  );
}

function FileList({ workspaceId, state }: { workspaceId: string; state: AsyncState<KnowledgeItemPage> }) {
  const { api } = usePlatform();
  const [deleting, setDeleting] = useState<KnowledgeItem | null>(null);
  const [problem, setProblem] = useState<Problem | null>(null);

  const openOriginal = async (item: KnowledgeItem) => {
    setProblem(null);
    try {
      const content = await api.getKnowledgeOriginal(workspaceId, item.id);
      saveBytes(content.bytes, item.name);
    } catch (error) {
      setProblem(toProblem(error));
    }
  };

  if (state.loading && !state.data) return <Spinner label="ファイルを取得しています" />;
  return (
    <div className="stack">
      <ProblemNotice problem={state.problem ?? problem} action={<Button onClick={state.reload}>再取得</Button>} />
      {state.data && state.data.items.length === 0 && (
        <EmptyState title="ファイルがまだありません" description="資料を追加すると、Studio の AI が丸ごと読めるようになります。" />
      )}
      {state.data && state.data.items.length > 0 && (
        <Table
          label="アップロードしたファイル一覧"
          columns={[
            { key: 'name', header: '名前' },
            { key: 'mediaType', header: '形式' },
            { key: 'size', header: '大きさ' },
            { key: 'status', header: '状態' },
            { key: 'refs', header: '参照' },
            { key: 'updatedAt', header: '更新' },
            { key: 'actions', header: '操作' },
          ]}
          rows={state.data.items.map((item) => {
            const status = knowledgeItemStatusView(item.status);
            return {
              key: item.id,
              cells: {
                name: item.name,
                mediaType: <code>{item.mediaType}</code>,
                size: formatBytes(item.byteSize),
                status: (
                  <span className="status-cell">
                    <Badge tone={status.tone}>{status.label}</Badge>
                    {item.failedReason && <span className="hint">{item.failedReason}</span>}
                  </span>
                ),
                refs: item.refCount,
                updatedAt: formatDateTime(item.updatedAt),
                actions: (
                  <span className="row">
                    {/* 読み上げでは行が分からないので、名前にファイル名を含める（E2E の手順もこの名前で押す）。 */}
                    <Button
                      variant="text"
                      aria-label={`${item.name} の原本を開く`}
                      disabled={item.status !== 'ready'}
                      onClick={() => void openOriginal(item)}
                    >
                      原本を開く
                    </Button>
                    <Button variant="text" aria-label={`${item.name} を削除`} onClick={() => setDeleting(item)}>
                      削除
                    </Button>
                  </span>
                ),
              },
            };
          })}
        />
      )}
      {deleting && (
        <DeleteDialog
          kind="ファイル"
          name={deleting.name}
          consequences={['このファイルの原本', 'Studio の AI がこのファイルを読めなくなります']}
          describeFailure={(problem) =>
            problem.code === 'item_referenced'
              ? 'Draft の出典として参照されているので削除できません。出典を書き換えてから削除してください。'
              : null
          }
          onDelete={async (idempotencyKey) => {
            await api.deleteKnowledgeItem(workspaceId, deleting.id, idempotencyKey);
            setDeleting(null);
            state.reload();
          }}
          onClose={() => setDeleting(null)}
        />
      )}
    </div>
  );
}

function SearchResults({ workspaceId, query }: { workspaceId: string; query: string }) {
  const { api } = usePlatform();
  const result = useAsync(() => api.searchKnowledge(workspaceId, query), [api, workspaceId, query]);
  if (result.loading) return <Spinner label="検索しています" />;
  return (
    <div className="stack">
      <ProblemNotice problem={result.problem} action={<Button onClick={result.reload}>再検索</Button>} />
      {result.data && result.data.hits.length === 0 && <EmptyState title="一致するファイルがありません" />}
      {result.data && result.data.hits.length > 0 && (
        <Table
          label="検索結果"
          columns={[
            { key: 'name', header: '名前' },
            { key: 'kind', header: 'ソース' },
            { key: 'mediaType', header: '形式' },
          ]}
          rows={result.data.hits.map((hit) => ({
            key: `${hit.sourceId}:${hit.ref}`,
            cells: { name: hit.name, kind: <code>{hit.kind}</code>, mediaType: <code>{hit.mediaType}</code> },
          }))}
        />
      )}
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
}
